import UPButton from 'components/button';
import { UPDropdown } from 'components/dropdown';
import UPInput from 'components/input';
import { countriesWithBelgiumFirst } from 'modules/companies/helpers';
import { OnboardingFlowContext } from 'modules/onboarding/OnboardingFlow';
import { AddressStepFieldNames, OnboardingFlowAddressStepFields } from 'modules/onboarding/types';
import React, { useContext, useEffect, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useAppDispatch, useAppSelector } from 'store';
import { sendOnboardingAddressStepAction } from 'store/actions/company-onboarding-actions';
import { countriesNames } from 'store/selectors/countries-selectors';
import {
  COMPANY_INFORMATION__CITY,
  COMPANY_INFORMATION__POSTAL_CODE,
  COMPANY_INFORMATION__STREET_NAME,
  COMPANY_INFORMATION__STREET_NUMBER,
  GENERAL__COUNTRY,
  ONBOARDING__NEXT_STEP,
  ONBOARDING__POST_BOX,
  ONBOARDING__PREVIOUS_STEP,
} from 'translations/constants';
import { getInputErrorText } from 'utils/helpers';
import {
  OnboardingFlowCard,
  OnboardingFlowCardActionsContainer,
  OnboardingFlowCardContentContainer,
  OnboardingFlowCardContentRow,
  OnboardingFlowCardField,
  OnboardingFlowStepContainer,
} from './styles';

const FormDefaultValues: OnboardingFlowAddressStepFields = {
  streetName: '',
  houseNumber: '',
  postBox: '',
  zipCode: '',
  city: '',
  countryId: null,
};

const AddressStep: React.FC = () => {
  const { t, activeStep, onClickNextStep, onClickPreviousStep, onboardingData, companyIdNumber, setOnboardingData } =
    useContext(OnboardingFlowContext);

  const dispatch = useAppDispatch();
  const countries = countriesWithBelgiumFirst(useAppSelector(countriesNames));

  const [loading, setLoading] = useState(false);

  const {
    getValues,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<OnboardingFlowAddressStepFields>({
    defaultValues: onboardingData?.address || FormDefaultValues,
  });

  const onProceed: SubmitHandler<OnboardingFlowAddressStepFields> = async data => {
    setLoading(true);
    await dispatch(sendOnboardingAddressStepAction({ data, companyIdNumber })).then(() => {
      setOnboardingData({
        ...onboardingData,
        address: data,
      });
      onClickNextStep();
    });
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  };

  const onSubmit = () => {
    handleSubmit(onProceed)();
  };

  useEffect(() => {
    if (onboardingData?.address) {
      reset(onboardingData.address);
    }
  }, [onboardingData, reset]);

  return (
    <OnboardingFlowStepContainer activeStep={activeStep}>
      <OnboardingFlowCard>
        <OnboardingFlowCardContentContainer>
          <OnboardingFlowCardContentRow container xs={12}>
            <OnboardingFlowCardField item xs={6}>
              <Controller
                name={AddressStepFieldNames.STREET_NAME}
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <UPInput
                    label={t(COMPANY_INFORMATION__STREET_NAME)}
                    value={field.value}
                    placeholder={t(COMPANY_INFORMATION__STREET_NAME)}
                    onChange={field.onChange}
                    required
                    error={!!errors.streetName}
                    helperText={getInputErrorText(errors, AddressStepFieldNames.STREET_NAME)}
                  />
                )}
              />
            </OnboardingFlowCardField>
            <OnboardingFlowCardField item xs={2}>
              <Controller
                name={AddressStepFieldNames.HOUSE_NUMBER}
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <UPInput
                    label={t(COMPANY_INFORMATION__STREET_NUMBER)}
                    value={field.value}
                    placeholder={t(COMPANY_INFORMATION__STREET_NUMBER)}
                    onChange={field.onChange}
                    required
                    error={!!errors.houseNumber}
                    helperText={getInputErrorText(errors, AddressStepFieldNames.HOUSE_NUMBER)}
                  />
                )}
              />
            </OnboardingFlowCardField>
            <OnboardingFlowCardField item xs={2}>
              <Controller
                name={AddressStepFieldNames.POST_BOX}
                control={control}
                render={({ field }) => (
                  <UPInput
                    label={t(ONBOARDING__POST_BOX)}
                    value={field.value}
                    placeholder={t(ONBOARDING__POST_BOX)}
                    onChange={field.onChange}
                  />
                )}
              />
            </OnboardingFlowCardField>
          </OnboardingFlowCardContentRow>
          <OnboardingFlowCardContentRow container xs={12}>
            <OnboardingFlowCardField item xs={3}>
              <Controller
                name={AddressStepFieldNames.ZIP_CODE}
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <UPInput
                    label={t(COMPANY_INFORMATION__POSTAL_CODE)}
                    value={field.value}
                    placeholder={t(COMPANY_INFORMATION__POSTAL_CODE)}
                    onChange={field.onChange}
                    required
                    error={!!errors.zipCode}
                    helperText={getInputErrorText(errors, AddressStepFieldNames.ZIP_CODE)}
                  />
                )}
              />
            </OnboardingFlowCardField>
            <OnboardingFlowCardField item xs={3}>
              <Controller
                name={AddressStepFieldNames.CITY}
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <UPInput
                    label={t(COMPANY_INFORMATION__CITY)}
                    value={field.value}
                    placeholder={t(COMPANY_INFORMATION__CITY)}
                    onChange={field.onChange}
                    required
                    error={!!errors.city}
                    helperText={getInputErrorText(errors, AddressStepFieldNames.CITY)}
                  />
                )}
              />
            </OnboardingFlowCardField>
            <OnboardingFlowCardField item xs={4}>
              <Controller
                name={AddressStepFieldNames.COUNTRY_ID}
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <UPDropdown
                    label={t(GENERAL__COUNTRY)}
                    value={field.value}
                    items={countries}
                    placeholder={t(GENERAL__COUNTRY)}
                    onChange={field.onChange}
                    required
                    error={!!errors.countryId}
                    helperText={getInputErrorText(errors, AddressStepFieldNames.COUNTRY_ID)}
                  />
                )}
              />
            </OnboardingFlowCardField>
          </OnboardingFlowCardContentRow>
        </OnboardingFlowCardContentContainer>
        <OnboardingFlowCardActionsContainer>
          <UPButton
            text={t(ONBOARDING__PREVIOUS_STEP)}
            onClick={() => {
              setOnboardingData({
                ...onboardingData,
                address: getValues(),
              });
              onClickPreviousStep();
            }}
            outlined
          />
          <UPButton text={t(ONBOARDING__NEXT_STEP)} onClick={onSubmit} disabled={loading} />
        </OnboardingFlowCardActionsContainer>
      </OnboardingFlowCard>
    </OnboardingFlowStepContainer>
  );
};

export default AddressStep;
